import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';

import { useMutationHTTPRequest } from 'api/hooks/useHTTPRequest';

import { GenerateNoteMutationResponse } from 'features/sessions/config/types';
import { useSessionNoteSettings } from 'features/sessions/hooks/useSessionNoteSettings';
import { useSessionsQueryActions } from 'features/sessions/hooks/useSessionsQueryActions';

import { showSuccessToast } from 'shared/components/Toast';

type GenerateNoteMutationProps = {
  sessionId: string;
};

export const useGenerateNoteMutation = ({
  sessionId,
}: GenerateNoteMutationProps) => {
  const { request } = useMutationHTTPRequest();

  const { setValue } = useFormContext();
  const { getSettings } = useSessionNoteSettings();
  const { updateSession, getSessionById } = useSessionsQueryActions();

  return useMutation<
    GenerateNoteMutationResponse,
    any,
    { noteStructure: string }
  >(
    async ({ noteStructure }) => {
      const settings = getSettings();

      return await request({
        url: `/api/coach/coaching-sessions/${sessionId}/transcript/insights/generate`,
        body: {
          insightsStructureCode: noteStructure,
          useTranscriptLanguage: settings.useTranscriptLanguage,
        },
      });
    },
    {
      onSuccess: ({ transcriptAnalysis }, { noteStructure }) => {
        setValue('summary.transcriptAnalysis', transcriptAnalysis);
        setValue('summary.insightsStructureCode', noteStructure);

        const prevSession = getSessionById(sessionId!);
        if (!prevSession) {
          return;
        }

        showSuccessToast('Note has been generated.');

        updateSession(sessionId, {
          ...prevSession,
          summary: {
            ...prevSession?.summary,
            // @ts-ignore
            insightsStructureCode: noteStructure,
            transcriptAnalysis,
          },
        });
      },

      useErrorBoundary: false,
      retry: 1,
    }
  );
};
